import {
  mdiAccountOutline,
  mdiArchiveSearchOutline,
  mdiDomain,
  mdiEmailOutline,
  mdiForumOutline,
  mdiInformationOutline,
  mdiMessageTextOutline,
  // mdiPresentation,
} from '@mdi/js';
import Icon from '@mdi/react';
import { throttle } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { environment } from '../../../environments/environment';
import { ReactComponent as MenuChecksIcon } from '../../assets/images/icons/menu-checks.svg';
import { ReactComponent as MenuCheckupIcon } from '../../assets/images/icons/menu-checkup.svg';
import { AuthContext } from '../../contexts/auth.context';
import { useKirbyChecks, useKirbyInfoGeneral } from '../../hooks/useKirbyData';
import { useOrganizationAbility } from '../../hooks/useOrganizationAbility';
import * as Styled from './Sidebar.styles';
import { useTranslation } from 'react-i18next';

const Sidebar: React.FC = () => {
  const { auth } = useContext(AuthContext);
  const org = auth.org;
  const { data: checks } = useKirbyChecks();
  const { data: info } = useKirbyInfoGeneral();
  const location = useLocation();
  const { t } = useTranslation();

  const ability = useOrganizationAbility();

  const wrapperEl = useRef<HTMLDivElement>(null);
  const topEl = useRef<HTMLDivElement>(null);
  const [topMargin, setTopMargin] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);
  const tutorialSubMenuVisible = location.pathname.startsWith('/tutorials-demo');
  // const isOnSeminarPage = useMatch('/seminars/free');
  // const isOnTutorialPage = useMatch('/tutorials/*');
  // const tutorialSubMenuVisible = isOnSeminarPage || isOnTutorialPage;


  useEffect(() => {
    const handler = throttle(() => {
      if (window.innerWidth < 768) {
        // Nothing to do on mobile
        return;
      }
      if (!wrapperEl.current) {
        return;
      }
      const offsetY = wrapperEl.current.getBoundingClientRect().y;
      if (offsetY > 0) {
        // If the offset is negative, that means it's no longer sticky
        setTopMargin(offsetY);
      }
      setIsScrollable(topEl.current.scrollHeight > topEl.current.clientHeight);
    }, 0);

    window.addEventListener('scroll', handler, { passive: true, capture: true });
    window.addEventListener('resize', handler, { passive: true, capture: true });
    return () => {
      window.removeEventListener('scroll', handler);
      window.removeEventListener('resize', handler);
    };
  }, []);

  const tutorialChecks = checks?.filter(check => check.show_in_tutorial) ?? [];

  return (
    <Styled.Sidebar ref={wrapperEl} style={{ maxHeight: `calc(100vh - ${topMargin}px)` }}>
      <Styled.Top ref={topEl}>
        <Styled.Item to='/'>
          <MenuCheckupIcon />
          {t('sidebar.home')}
        </Styled.Item>

        <Styled.Item to='/checks'>
          <MenuChecksIcon />
          {t('sidebar.checks')}
        </Styled.Item>

        {org && (
          <>
            {ability.can('read', 'Organization', 'checks') && (
              <Styled.Item to='/org/checks'>
                <Icon path={mdiArchiveSearchOutline} size='20px' />
                {t('sidebar.orgChecks')}
              </Styled.Item>
            )}

            {ability.can('read', 'Organization', 'analytics') && (
              <Styled.Item to='/org/analytics' className={location.pathname === '/org/users' ? 'active' : undefined}>
                <Icon path={mdiDomain} size='20px' />
                {t('sidebar.orgAnalytics')}
              </Styled.Item>
            )}
          </>
        )}

        <Styled.Item to='/settings'>
          <Icon path={mdiAccountOutline} size='20px' />
          {t('sidebar.settings')}
        </Styled.Item>
        {/*
        {
          info.online_demo &&
          <Styled.Item to='/seminars/free'>
            <Icon path={mdiPresentation} size='20px' />
            {t('sidebar.seminarsFree')}
          </Styled.Item>
        }
        {(tutorialChecks.length > 0 ?? false) && (
          <>
          <Styled.Item to='/tutorials'>
            <Icon path={mdiInformationOutline} size='20px' />
            {t('sidebar.tutorials')}
          </Styled.Item>
          {tutorialSubMenuVisible && (
            <>
              {(tutorialChecks ?? []).map(check => (
                <Styled.Item $isSubItem key={check.slug} to={`/tutorials/${check.slug}`}>
                  {check.title}
                </Styled.Item>
              ))}
            </>
          )}
          </>
        )}
        */}
        {((info?.online_demo ?? false) || tutorialChecks.length > 0) && (
          <>
            <Styled.Item to='/tutorials-demo'>
              <Icon path={mdiInformationOutline} size='20px' />
              {t('sidebar.tutorialsDemo')}
            </Styled.Item>
            {tutorialSubMenuVisible &&
              tutorialChecks.map(check => (
                <Styled.Item $isSubItem key={check.slug} to={`/tutorials-demo/${check.slug}`}>
                  {check.title}
                </Styled.Item>
              ))}
          </>
        )}

        {/*<Styled.Item to='/seminars/regular'>*/}
        {/*  <MenuSeminarsIcon />*/}
        {/*  {t('sidebar.seminarsRegular')}*/}
        {/*</Styled.Item>*/}
      </Styled.Top>
      <Styled.Bottom $isScrollable={isScrollable}>
        <Styled.Item to='https://aebcom.typeform.com/to/iXIE9y9d' target='_blank' rel='noopener noreferrer'>
          <Icon path={mdiMessageTextOutline} size='20px' />
          {t('sidebar.feedback')}
        </Styled.Item>
        <Styled.Item to={environment.discourse.baseUrl}>
          <Icon path={mdiForumOutline} size='20px' />
          {t('sidebar.forum')}
        </Styled.Item>
        <Styled.Item to='mailto:info@solid-app.de'>
          <Icon path={mdiEmailOutline} size='20px' />
          {t('sidebar.contact')}
        </Styled.Item>
      </Styled.Bottom>
    </Styled.Sidebar>
  );
};

export default Sidebar;
